import { useEffect, type ReactNode } from 'react';
import { GlobalStyles } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { useFeature } from '../admin/featureFlags/FeatureGate';
import { appTheme } from '../theme';
import { createMockupTheme, mockupGlobalStyles } from './mockupTheme';
import { MOCKUP_LOOK_ATTR, MOCKUP_LOOK_FEATURE } from './tokens';

/**
 * Wygląd z makiety (status 28.10.2026 pkt 3, decyzja użytkownika 28.10.2026,
 * przełącznik `mockupLook`, S-28.10-01) — opakowanie układu panelu.
 *
 * Włączony: motyw MUI z makiety (`mockupTheme.ts`) nad motywem aplikacji
 * i style globalne zaczepów. Wyłączony (także zanim przyjdą przełączniki):
 * dzieci bez zmian — wygląd jak dotąd, ten sam motyw, który daje `<Admin>`.
 * Okna i dymki otwierane z ekranów są w tym samym drzewie Reacta, więc
 * dostają ten sam motyw.
 */
export const MockupLookTheme = ({ children }: { children: ReactNode }) => {
  const on = useFeature(MOCKUP_LOOK_FEATURE);
  // Atrybut także na <body>: okna, menu i dymki renderują się poza układem,
  // a style zaczepów (np. karta sekcji w oknie) mają działać i tam.
  useEffect(() => {
    document.body.setAttribute(MOCKUP_LOOK_ATTR, on ? 'on' : 'off');
    return () => document.body.removeAttribute(MOCKUP_LOOK_ATTR);
  }, [on]);
  // To samo drzewo w obu stanach — przyjście przełączników po zalogowaniu
  // nie przemontowuje układu (stan ekranu i menu zostaje).
  return (
    <ThemeProvider theme={on ? createMockupTheme(appTheme) : keepOuterTheme}>
      {on ? <GlobalStyles styles={mockupGlobalStyles} /> : null}
      <div {...{ [MOCKUP_LOOK_ATTR]: on ? 'on' : 'off' }} style={{ display: 'contents' }}>
        {children}
      </div>
    </ThemeProvider>
  );
};

/** Wyłączony: motyw z `<Admin>` bez żadnej zmiany. */
const keepOuterTheme = <T,>(outer: T): T => outer;
